// ============================================================================
// ClassInfoButton (L05) — popover « definition de la classe » (lecture seule)
// ============================================================================
// Port FIDELE du PoC components/salary/class-info-button.tsx (81 L @ SHA 7e40e4a),
// consomme par EmployeeGrid. Icone Info posee sur la ligne d'une classe : devoile
// en popover la description generale + les requis de chaque niveau. Masquee si la
// classe n'a aucun texte a montrer (docs herites sans description).
//
// TRANSPOSITION (PLAN-SOUS-LOTS §SL-C, PLAN-ATTAQUE) :
//  - `Button` @/components/ui → bouton natif + classe icone dark-aware (le parc
//    gest n'a pas de primitive Button ; motif du board pour les boutons icone) ;
//  - tokens shadcn (bg-card/text-foreground/border-border) → slate dark-aware ;
//  - libelles FR en dur → cles i18n FR+EN (P13). Regle #4 : zero emoji ;
//    Regle #28 : tokens dark-aware, zero CSS en dur cote couleur.

import { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';

import { useTranslation } from '../../i18n';
import type { SalaryClassNiveau } from '../../lib/types/firestore';

// Bouton icone compact (motif partage grille — remplace `Button variant=ghost`).
const iconBtn =
  'inline-flex size-6 items-center justify-center rounded-md text-slate-400 transition-colors ' +
  'hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300 touch-manipulation';

export function ClassInfoButton({
  description,
  niveaux,
}: {
  description: string | null;
  niveaux: SalaryClassNiveau[];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const niveauxWithDesc = niveaux.filter((n) => (n.description ?? '').trim() !== '');
  const hasContent = (description ?? '').trim() !== '' || niveauxWithDesc.length > 0;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!hasContent) return null;

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        className={iconBtn}
        aria-label={t('gest:planification.classInfo.aria')}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Info size={16} aria-hidden="true" />
      </button>

      {open ? (
        <div className="absolute left-0 top-full z-20 mt-1 w-80 max-w-[min(90vw,24rem)] rounded-lg border border-slate-200 bg-white p-3 text-left text-field text-slate-800 shadow-md dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
          {description && description.trim() !== '' ? (
            <p className="whitespace-pre-line text-slate-500 dark:text-slate-400">{description}</p>
          ) : null}

          {niveauxWithDesc.length > 0 ? (
            <ul className="mt-3 flex flex-col gap-3">
              {niveauxWithDesc.map((n) => (
                <li key={n.level} className="flex flex-col gap-1">
                  <span className="text-meta font-semibold text-slate-800 dark:text-slate-100">
                    {t('gest:planification.classInfo.niveau', { level: n.level })}
                    {n.label ? ` — ${n.label}` : ''}
                  </span>
                  <span className="whitespace-pre-line text-meta text-slate-500 dark:text-slate-400">
                    {n.description}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
